//
// ============================================================================
// TYPES
// ============================================================================
//

/**
 * Error type classification for different handling strategies
 */
export type ErrorType =
  | "abort" // Request was cancelled (user navigation, abort signal)
  | "timeout" // Request timed out (server too slow, gateway timeout)
  | "network" // Network/connectivity issues (CORS, offline, DNS)
  | "server" // Server errors (500, 502, 503, 504)
  | "validation" // Business logic validation errors (400)
  | "unknown"; // Unclassified errors — includes 401/403 (auth middleware handles redirects)

/**
 * Options for configuring error handling behavior
 */
export interface ErrorHandlerOptions {
  /**
   * Operation name for logging and debugging
   * Example: "fetchPendingSamples", "acceptResult", "updateBarcode"
   */
  operation: string;

  /**
   * Automatically show error notification to user
   * Default: false
   */
  showToast?: boolean;

  /**
   * i18n translation key for fallback error message
   * Used when specific error cannot be classified
   * Example: "components.common.errors.fetchFailed" or "pages.validation.errors.fetchFailed"
   */
  fallbackKey?: string;

  /**
   * Custom callback for additional error handling
   * Receives the final user-friendly error message
   * Example: Update component-specific error state
   */
  onError?: (message: string) => void;

  /**
   * Include operation name in error message shown to user
   * Useful for debugging but may be too technical
   * Default: false
   */
  includeOperation?: boolean;
}

/**
 * Structured error result returned by handleError
 */
export interface ErrorResult {
  /**
   * Classified error type for conditional handling
   */
  type: ErrorType;

  /**
   * User-friendly localized error message
   */
  message: string;

  /**
   * Original error object for debugging
   */
  originalError: unknown;

  /**
   * HTTP status code if available
   */
  statusCode?: number;
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Centralized Error Handling Composable
 *
 * Purpose:
 * Provides consistent error handling across all API composables and components.
 * Eliminates duplicated catch blocks and error parsing logic.
 *
 * Responsibilities:
 * - Parse errors from various formats (Error objects, strings, API responses)
 * - Classify errors by type (network, server, auth, abort, unknown)
 * - Generate user-friendly localized error messages
 * - Optionally display snackbar notifications
 * - Log errors to console with operation context
 * - Return structured error info for custom handling
 */
export const useErrorBoundary = () => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Snackbar notifications
  const { showSnackbar } = useSnackbar();

  //
  // ============================================================================
  // ERROR CLASSIFICATION
  // ============================================================================
  //

  /**
   * Classify error by type for appropriate handling
   *
   * @param error - Error object or string
   * @returns Classified error type
   */
  const classifyError = (error: unknown): ErrorType => {
    // Narrow to object-like for property access
    const err = error as Record<string, unknown> | null | undefined;

    // Handle AbortError from AbortController
    if (err?.name === "AbortError") {
      return "abort";
    }

    // Extract error message from various formats
    const dataObj = err?.data as Record<string, unknown> | undefined;
    const errorStr =
      typeof error === "string"
        ? error
        : (err?.message as string) || (dataObj?.message as string) || "";
    const errorLower = errorStr.toLowerCase();

    // Extract HTTP status code
    const responseObj = err?.response as Record<string, unknown> | undefined;
    const statusCode =
      (err?.statusCode as number) || (err?.status as number) || (responseObj?.status as number);

    // Check for validation errors
    if (statusCode === 400) {
      return "validation";
    }

    // Check for server errors by status code FIRST — most authoritative signal.
    // Must run before message-based heuristics because ofetch error messages
    // for 500 responses often contain "no response" which would incorrectly
    // match the network check below.
    if (statusCode >= 500) {
      return "server";
    }

    // Check for request timeout
    if (errorLower.includes("timeout") || errorLower.includes("timed out")) {
      return "timeout";
    }

    // Check for network connectivity issues (message-based fallback)
    if (
      errorLower.includes("failed to fetch") ||
      errorLower.includes("network") ||
      errorLower.includes("no response") ||
      errorLower.includes("cors")
    ) {
      return "network";
    }

    // Fallback: server errors detected by message (no statusCode available)
    if (
      errorStr.includes("500") ||
      errorStr.includes("502") ||
      errorStr.includes("503") ||
      errorStr.includes("504") ||
      errorLower.includes("internal server error")
    ) {
      return "server";
    }

    return "unknown";
  };

  /**
   * Get localized error message based on error type
   *
   * @param type - Classified error type
   * @param fallbackKey - Custom fallback translation key
   * @param originalMessage - Original error message for unknown errors
   * @returns Localized user-friendly message
   */
  const getErrorMessage = (
    type: ErrorType,
    fallbackKey?: string,
    originalMessage?: string,
  ): string => {
    switch (type) {
      case "abort":
        // Silent - no message needed for user-initiated cancellation
        return "";

      case "timeout":
        // Timeout errors - server too slow or gateway timeout
        return t("components.common.errors.timeoutError");

      case "network":
        // Network errors - suggest checking connection
        return t("pages.validation.results.pending.errors.networkError");

      case "server":
        // Server errors - suggest trying again later
        return t("pages.validation.results.pending.errors.serverError");

      case "validation":
        // Validation errors - show specific message from API
        return (
          originalMessage ||
          t("components.common.errors.validationError", "Invalid data. Please check your input.")
        );

      case "unknown":
      default:
        // Unknown errors - use fallback or show original message
        if (fallbackKey) {
          return t(fallbackKey);
        }
        // Only show original message if it's user-friendly (not stack trace)
        if (
          originalMessage &&
          !originalMessage.includes("Error:") &&
          originalMessage.length < 200
        ) {
          return originalMessage;
        }
        return t("pages.validation.results.pending.errors.pleaseTryAgain");
    }
  };

  //
  // ============================================================================
  // MAIN ERROR HANDLER
  // ============================================================================
  //

  /**
   * Handle error with classification, logging, and optional notification
   *
   * @param error - Error object, string, or any thrown value
   * @param options - Configuration for error handling behavior
   * @returns Structured error result with type and message
   */
  const handleError = (error: unknown, options: ErrorHandlerOptions): ErrorResult => {
    // Narrow to object-like for property access
    const err = error as Record<string, unknown> | null | undefined;

    // Step 1: Classify error type
    const type = classifyError(error);

    // Step 2: Extract original message
    const dataObj = err?.data as Record<string, unknown> | undefined;
    const originalMessage =
      typeof error === "string"
        ? error
        : (err?.message as string) || (dataObj?.message as string) || "";

    // Step 3: Get user-friendly localized message
    const message = getErrorMessage(type, options.fallbackKey, originalMessage);

    // Step 4: Extract status code
    const responseObj = err?.response as Record<string, unknown> | undefined;
    const statusCode =
      (err?.statusCode as number) || (err?.status as number) || (responseObj?.status as number);

    // Step 5: Log to console for debugging
    const logPrefix = `[${options.operation}]`;
    const logMessage = type === "abort" ? "Request aborted" : `${type.toUpperCase()}: ${message}`;

    console.error(logPrefix, logMessage, error);

    // Step 6: Show toast notification if requested
    if (options.showToast && message && type !== "abort") {
      showSnackbar(options.includeOperation ? `${options.operation}: ${message}` : message, {
        color: "error",
        icon: "mdi-alert-circle-outline",
      });
    }

    // Step 7: Call custom error handler
    if (options.onError && message) {
      options.onError(message);
    }

    // Step 8: Return structured result
    return {
      type,
      message,
      originalError: error,
      statusCode,
    };
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    /**
     * Handle error with classification and optional notification
     */
    handleError,

    /**
     * Classify error type without handling (for conditional logic)
     */
    classifyError,

    /**
     * Get localized message for error type (for custom UIs)
     */
    getErrorMessage,
  };
};
